import type { FindingData } from "@/lib/chat-types";
import { MarkdownTextBlock } from "./MarkdownTextBlock";

const severityCfg = {
  critical: { label: "Critical", color: "text-red-400", bg: "bg-red-500/10" },
  warning: { label: "Warning", color: "text-amber-400", bg: "bg-amber-500/10" },
  pass: { label: "Pass", color: "text-green-400", bg: "bg-green-500/10" },
} as const;

function BookIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2Z" />
      <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7Z" />
    </svg>
  );
}

export function FindingsGrid({
  findings,
  onViewEvidence,
}: {
  findings: FindingData[];
  onViewEvidence?: (finding: FindingData) => void;
}) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 my-2">
      {findings.map((f, i) => {
        const c = severityCfg[f.severity];
        return (
          <div key={i} className="px-3.5 py-3 border border-[var(--color-border)] rounded-[10px] bg-[var(--color-surface)]">
            <div className="flex items-center gap-2 mb-1.5">
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${c.bg} ${c.color}`}
                style={{ fontFamily: "var(--font-mono)" }}
              >
                {f.severity === "critical" && (
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></svg>
                )}
                {f.severity === "warning" && (
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
                )}
                {f.severity === "pass" && (
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
                )}
                {c.label}
              </span>
              <span className="text-[10px] text-[var(--color-fg-tertiary)]" style={{ fontFamily: "var(--font-mono)" }}>{f.regulation}</span>
            </div>
            <div className="mb-1 [&_p]:text-[13px] [&_p]:font-medium [&_p]:text-[var(--color-fg)] [&_p]:leading-snug [&_p]:mb-0">
              <MarkdownTextBlock content={f.issue} />
            </div>
            <div className="[&_p]:text-xs [&_p]:text-[var(--color-fg-tertiary)] [&_p]:leading-relaxed [&_p]:mb-0">
              <MarkdownTextBlock content={f.recommendation} />
            </div>
            {f.evidenceRefs && f.evidenceRefs.length > 0 && onViewEvidence && (
              <button
                type="button"
                onClick={() => onViewEvidence(f)}
                className="mt-3 inline-flex items-center gap-1.5 rounded-[10px] border border-[var(--color-border)] bg-[var(--color-border)]/45 px-2.5 py-1.5 text-[12px] text-[var(--color-accent)] hover:bg-[var(--color-border)] cursor-pointer"
              >
                <BookIcon />
                <span>View Regulation</span>
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
